import { recordEntries } from '@seen/core/utils'

import { Button, cn, KBD, TypographyDetail } from '@seen/ui'
import { IconFilter } from '@tabler/icons-react'
import {
  type Dispatch,
  type JSX,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ComboboxMultiCommand } from '#src/components/core/combobox/ComboboxMulti.tsx'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import Filters, {
  AnimateChangeInHeight,
  type Filter,
  type FilterConfig,
} from '#src/components/filter/Filter.tsx'
import {
  FILTER_OPERATORS,
  type FilterOperator,
} from '#src/components/filter/operators.ts'
import { FilterValue } from '#src/components/filter/useFilteredData.ts'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'

type FilterComposerProps<T, Fields extends string> = {
  filterOptions: {
    [Field in Fields]: FilterConfig<T>
  }
  filters: Filter<Fields>[]
  setFilters: Dispatch<SetStateAction<Filter<Fields>[]>>
}

// TODO: Consolidate with FilterComposer

export function FilterEditor<T, Fields extends string = string>({
  filterOptions,
  filters,
  setFilters,
}: FilterComposerProps<T, Fields>): JSX.Element {
  const [open, setOpen] = useState(false)
  const [selectedView, setSelectedView] = useState<Fields | null>(null)
  const [commandInput, setCommandInput] = useState('')
  const commandInputRef = useRef<HTMLInputElement>(null)

  const filtersInView = filters.filter((f) => f.field === selectedView)
  const anyFiltersApplied = filters.flatMap((f) => f.value).length > 0
  const [showFilterTriggerText, setShowFilterTriggerText] =
    useState(anyFiltersApplied)

  useEffect(() => {
    // Prevent content shift when the popover is opened by deferring the 'any filters applied'
    // check until the popover is closed
    if (open) {
      return
    }

    setShowFilterTriggerText(anyFiltersApplied)
  }, [anyFiltersApplied, open])

  const selectedFilterConfig = selectedView && filterOptions[selectedView]
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogValue, setDialogValue] = useState(
    filtersInView.find((f) => f.field === selectedView)?.value[0] ?? ''
  )

  useHotkeys(Hotkeys.Filter, () => setOpen(true), {
    preventDefault: true,
  })

  return (
    <div className="flex flex-wrap gap-2">
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) {
            setDialogValue('')
            setSelectedView(null)
          }
        }}
      >
        <DialogDisplay>
          <DialogHeader>
            <DialogTitle>Filter by {selectedFilterConfig?.label}…</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <DialogDescription>
              <Input
                value={dialogValue}
                onChange={(e) => {
                  setDialogValue(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && selectedView) {
                    setDialogOpen(false)
                    setDialogValue('')
                    setSelectedView(null)
                    setFilters((prev) => {
                      const notInView = (prev ?? []).filter(
                        (f) => f.field !== selectedView
                      )

                      return [
                        ...notInView,
                        {
                          field: selectedView,
                          value: [dialogValue],
                          operator: 'contains',
                        },
                      ]
                    })
                  }
                }}
              />
            </DialogDescription>
          </DialogContent>
        </DialogDisplay>
      </Dialog>
      <Popover
        open={open}
        onOpenChange={(open) => {
          setOpen(open)
          if (!open) {
            setTimeout(() => {
              setSelectedView(null)
              setCommandInput('')
            }, 200)
          }
        }}
      >
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                size="sm"
                className={cn(
                  'group flex h-6 items-center gap-1.5 rounded-sm text-xs transition',
                  showFilterTriggerText && 'w-6'
                )}
              >
                <IconFilter className="size-3 shrink-0 text-muted-foreground transition-all group-hover:text-primary" />
                {!showFilterTriggerText && 'Filter'}
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent className="flex items-center gap-2 px-2">
            <TypographyDetail className="text-muted-foreground">
              Filter
            </TypographyDetail>
            <KBD>F</KBD>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-72 p-0">
          <AnimateChangeInHeight>
            {!selectedView && (
              <Command>
                <CommandInput
                  placeholder={selectedView ? selectedView : 'Filter...'}
                  className="h-9"
                  value={commandInput}
                  onInputCapture={(e) => {
                    setCommandInput(e.currentTarget.value)
                  }}
                  ref={commandInputRef}
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  {recordEntries(filterOptions).map(
                    ([name, { icon, label }]) => (
                      <CommandItem
                        className="group flex items-center gap-2 text-muted-foreground"
                        key={name}
                        value={name}
                        keywords={[name, label ?? '']}
                        onSelect={(currentValue) => {
                          setSelectedView(currentValue as Fields)
                          const type =
                            filterOptions[currentValue as Fields].type

                          setCommandInput('')
                          if (
                            type === 'multi-select' ||
                            type === 'multi-select-array' ||
                            type === 'date'
                          ) {
                            commandInputRef.current?.focus()
                          } else if (type === 'text') {
                            setOpen(false)
                            setDialogOpen(true)
                          } else if (type === 'boolean') {
                            setFilters((prev) => {
                              const notInView = (prev ?? []).filter(
                                (f) => f.field !== currentValue
                              )
                              return [
                                ...notInView,
                                {
                                  field: currentValue as Fields,
                                  value: ['true'],
                                  operator: 'equals',
                                },
                              ]
                            })
                            setSelectedView(null)
                            setOpen(false)
                          }
                        }}
                      >
                        {icon}
                        <span className="text-accent-foreground">
                          {label ?? name}
                        </span>
                      </CommandItem>
                    )
                  )}
                </CommandList>
              </Command>
            )}

            {selectedView &&
              (selectedFilterConfig?.type === 'multi-select' ||
                selectedFilterConfig?.type === 'multi-select-array') && (
                <ComboboxMultiCommand
                  options={selectedFilterConfig.options.map((o) => ({
                    value: o.value,
                    label: o.label ?? o.value,
                    keywords: o.keywords,
                  }))}
                  onSelect={(values, close) => {
                    setFilters((prev) => {
                      const notInView = prev.filter(
                        (f) => f.field !== selectedView
                      )
                      const inView = prev.filter(
                        (f) => f.field === selectedView
                      )?.[0] ?? {
                        field: selectedView,
                        value: [],
                        operator: selectedFilterConfig.defaultOperator ?? 'in',
                      }
                      return [
                        ...notInView,
                        {
                          ...inView,
                          value: values,
                        },
                      ]
                    })
                    if (close) {
                      setSelectedView(null)
                      setOpen(false)
                    }
                  }}
                  selectedValues={filtersInView.flatMap((f) => f.value)}
                />
              )}
            {selectedView && selectedFilterConfig?.type === 'date' && (
              <DateFilterEditor
                filter={
                  filtersInView[0] ?? {
                    field: selectedView,
                    value: [''],
                    operator: 'equals',
                  }
                }
                onChange={(filter) => {
                  const notInView = filters.filter(
                    (f) => f.field !== selectedView
                  )
                  setFilters([...notInView, filter])
                  setSelectedView(null)
                  setOpen(false)
                }}
                // Add support for 'none' value
                showNoneOption={true}
              />
            )}
            {selectedView && selectedFilterConfig?.type === 'boolean' && (
              <div className="flex flex-col gap-2 p-2">
                <Combobox
                  options={[
                    { value: 'true', label: 'True', keywords: ['true', 'yes'] },
                    {
                      value: 'false',
                      label: 'False',
                      keywords: ['false', 'no'],
                    },
                  ]}
                  onSelect={(v) => {
                    setFilters((prev) => {
                      const notInView = (prev ?? []).filter(
                        (f) => f.field !== selectedView
                      )
                      return [
                        ...notInView,
                        {
                          field: selectedView,
                          value: [v],
                          operator: 'equals',
                        },
                      ]
                    })
                    setSelectedView(null)
                    setOpen(false)
                  }}
                >
                  <Button variant="outline" className="w-full text-left">
                    {(() => {
                      const val = filtersInView[0]?.value[0]
                      if (val === 'true') {
                        return 'True'
                      }
                      if (val === 'false') {
                        return 'False'
                      }
                      return 'Select...'
                    })()}
                  </Button>
                </Combobox>
              </div>
            )}
          </AnimateChangeInHeight>
        </PopoverContent>
      </Popover>
      <Filters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
      />
    </div>
  )
}

type DateFilterEditorProps<Fields extends string> = {
  filter: Filter<Fields>
  onChange: (filter: Filter<Fields>) => void
}

function DateFilterEditor<Fields extends string>({
  filter,
  onChange,
  showNoneOption = false,
}: DateFilterEditorProps<Fields> & { showNoneOption?: boolean }): JSX.Element {
  const operators = FILTER_OPERATORS.date
  const [operator, setOperator] = useState<FilterOperator>(
    filter.operator ?? 'equals'
  )
  const [value, setValue] = useState<string[]>(
    filter.value.length > 0 ? filter.value : ['']
  )

  useEffect(() => {
    // Reset value if operator changes
    if (operator === 'between' && value.length < 2) {
      setValue(['', ''])
    } else if (operator !== 'between' && value.length > 1) {
      setValue([value[0]])
    }
  }, [operator, value])

  const handleApply = (): void => {
    onChange({
      ...filter,
      operator,
      value,
    })
  }

  // Determine if operator is a simple relative operator (no params needed)
  const isSimpleRelativeOperator = [
    'is_today',
    'is_yesterday',
    'is_tomorrow',
    'is_past',
    'is_future',
    'is_this_week',
    'is_last_week',
    'is_next_week',
    'is_this_month',
    'is_last_month',
    'is_next_month',
  ].includes(operator)

  // Determine if operator is a parameterized relative operator (needs number input)
  const isParameterizedRelativeOperator = [
    'in_last_days',
    'in_next_days',
  ].includes(operator)

  // Use Combobox for operator selection
  const operatorOptions = operators.map((op) => ({
    value: op.value,
    label: op.label,
    keywords: [op.label.toLowerCase(), op.value],
  }))

  return (
    <div className="flex w-full max-w-full flex-col gap-2 p-2">
      <div className="flex w-full max-w-full flex-wrap items-center gap-2">
        <Combobox
          options={operatorOptions}
          onSelect={(val) => {
            const newOperator = val as FilterOperator
            setOperator(newOperator)

            // Auto-apply for simple relative operators
            const isSimple = [
              'is_today',
              'is_yesterday',
              'is_tomorrow',
              'is_past',
              'is_future',
              'is_this_week',
              'is_last_week',
              'is_next_week',
              'is_this_month',
              'is_last_month',
              'is_next_month',
            ].includes(newOperator)

            if (isSimple) {
              onChange({
                ...filter,
                operator: newOperator,
                value: [''],
              })
            }
          }}
        >
          <button
            type="button"
            className="w-full rounded border bg-background px-2 py-1 text-left text-xs"
          >
            {operatorOptions.find((op) => op.value === operator)?.label}
          </button>
        </Combobox>
        {(() => {
          if (isSimpleRelativeOperator) {
            return null
          }

          if (isParameterizedRelativeOperator) {
            return (
              <div className="max-w-[120px] min-w-[80px] flex-1">
                <Input
                  type="number"
                  min="1"
                  value={value[0] || ''}
                  onChange={(e) => setValue([e.target.value])}
                  placeholder="Days"
                  className="h-8 text-xs"
                />
              </div>
            )
          }

          if (operator === 'between') {
            return (
              <div className="flex flex-wrap items-center gap-2">
                <DatePicker
                  value={value[0] || null}
                  onChange={(v) => setValue([v ?? '', value[1] ?? ''])}
                  placeholder="From"
                />
                <DatePicker
                  value={value[1] || null}
                  onChange={(v) => setValue([value[0] ?? '', v ?? ''])}
                  placeholder="To"
                />
              </div>
            )
          }

          return (
            <div className="max-w-[160px] min-w-[120px] flex-1">
              <DatePicker
                value={value[0] || null}
                onChange={(v) => setValue([v ?? ''])}
                placeholder="Date"
              />
            </div>
          )
        })()}
      </div>
      {!isSimpleRelativeOperator && (
        <div className="flex justify-end gap-2">
          {showNoneOption && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-2"
              onClick={() => {
                onChange({
                  ...filter,
                  operator,
                  value: [FilterValue.None],
                })
              }}
            >
              Missing
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleApply}>
            Apply
          </Button>
        </div>
      )}
    </div>
  )
}
